import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { TeamService } from '../../../services/teamService';

const TeamMemberCard = ({ member, isEditable = false, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: member?.name || '',
    position: member?.position || '',
    bio: member?.bio || '',
    expertise: member?.expertise || []
  });
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleImageUpload = async (file) => {
    if (!file || !member?.id) return;

    setUploading(true);
    try {
      const imageUrl = await TeamService?.uploadPhoto(file, member?.id);
      
      // Update member with new image URL
      await TeamService?.updateTeamMember(member?.id, { image_url: imageUrl });
      
      onUpdate?.(member?.id, { ...member, image_url: imageUrl });
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!member?.id) return;

    setSaving(true);
    try {
      const updatedMember = await TeamService?.updateTeamMember(member?.id, editData);
      onUpdate?.(member?.id, updatedMember);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating member:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleExpertiseChange = (value) => {
    const expertiseArray = value?.split(',')?.map(skill => skill?.trim())?.filter(skill => skill?.length > 0);
    setEditData(prev => ({ ...prev, expertise: expertiseArray }));
  };

  return (
    <div className="bg-white rounded-3xl overflow-hidden shadow-elevation-2 hover:shadow-elevation-4 transition-all duration-500 transform hover:-translate-y-2 group">
      {/* Photo Section with Upload */}
      <div className="relative aspect-square overflow-hidden">
        {member?.image_url ? (
          <img
            src={member?.image_url}
            alt={member?.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/20 via-secondary/15 to-primary/10 flex items-center justify-center relative">
            <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
            <Icon name="User" size={64} className="text-primary/30 relative z-10" />
          </div>
        )}
        
        {/* Upload overlay */}
        {isEditable && (
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <label className="cursor-pointer bg-white text-primary px-4 py-2 rounded-lg font-medium hover:bg-primary hover:text-white transition-colors duration-200 flex items-center space-x-2">
              <Icon name="Upload" size={16} />
              <span>{uploading ? 'Uploading...' : 'Change Photo'}</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e?.target?.files?.[0];
                  if (file) {
                    setImageFile(file);
                    handleImageUpload(file);
                  }
                }}
                disabled={uploading}
              />
            </label>
          </div>
        )}
      </div>
      {/* Content Section */}
      <div className="p-8">
        {isEditing ? (
          <div className="space-y-4">
            <input
              type="text"
              value={editData?.name}
              onChange={(e) => setEditData(prev => ({ ...prev, name: e?.target?.value }))}
              className="w-full text-xl font-bold text-text-primary border-b-2 border-primary/20 focus:border-primary outline-none bg-transparent pb-2"
              placeholder="Full Name"
            />
            <input
              type="text"
              value={editData?.position}
              onChange={(e) => setEditData(prev => ({ ...prev, position: e?.target?.value }))}
              className="w-full text-primary font-semibold border-b border-primary/20 focus:border-primary outline-none bg-transparent pb-2"
              placeholder="Position"
            />
            <textarea
              value={editData?.bio}
              onChange={(e) => setEditData(prev => ({ ...prev, bio: e?.target?.value }))}
              className="w-full text-text-secondary resize-none border border-gray-200 rounded-lg p-3 focus:border-primary outline-none"
              rows={4}
              placeholder="Biography"
            />
            <input
              type="text"
              value={editData?.expertise?.join(', ') || ''}
              onChange={(e) => handleExpertiseChange(e?.target?.value)}
              className="w-full text-sm border border-gray-200 rounded-lg p-3 focus:border-primary outline-none"
              placeholder="Expertise (comma separated)"
            />
            <div className="flex space-x-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="flex-1 bg-gray-200 text-text-primary px-4 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Name and Position */}
            <div className="mb-4">
              <h3 className="text-2xl font-bold text-text-primary mb-2 group-hover:text-primary transition-colors duration-300">
                {member?.name}
              </h3>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-0.5 bg-gradient-to-r from-primary to-secondary"></div>
                <p className="text-primary font-semibold text-lg">
                  {member?.position}
                </p>
              </div>
            </div>

            {/* Bio */}
            <p className="text-text-secondary leading-relaxed mb-6 text-base">
              {member?.bio}
            </p>

            {/* Expertise Tags */}
            {member?.expertise && member?.expertise?.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Icon name="Star" size={16} className="text-primary" />
                  <p className="text-sm font-semibold text-text-primary">Areas of Expertise</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {member?.expertise?.map((skill, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1.5 bg-gradient-to-r from-primary/10 to-secondary/10 text-primary text-sm rounded-full font-medium border border-primary/20 hover:shadow-sm transition-all duration-200"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Edit Button */}
            {isEditable && (
              <button
                onClick={() => setIsEditing(true)}
                className="mt-6 w-full bg-gradient-to-r from-primary to-secondary text-white px-4 py-2 rounded-lg font-medium hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <Icon name="Edit" size={16} />
                <span>Edit Member</span>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TeamMemberCard;